/**
 * IssueDrawer — 149-bz.
 *
 * Right-side drawer untuk Create / Edit Critical Issue dgn 5 seksi:
 *  1. Informasi Utama
 *  2. Dampak & Prioritas
 *  3. Kepemilikan & Target
 *  4. Referensi & Lampiran
 *  5. Tindakan Lanjutan (follow-up ringan yg tidak commit ke decision)
 *
 * Reuse endpoint existing: POST/PATCH /critical-issues. Attachment upload via
 * POST /critical-issues/{iid}/attachments. Reference picker menggunakan search
 * ke resource existing (projects, assets, users, tenders, tasks, reminders,
 * integrity findings) — TIDAK menggandakan data, hanya menyimpan id + label.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Info, AlertTriangle, UserCog, Paperclip, ListChecks, Upload, Trash2, Link2, ChevronDown, Loader2 } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SEV_OPTS = [
  { v: "low", label: "Low" },
  { v: "medium", label: "Medium" },
  { v: "high", label: "High" },
  { v: "critical", label: "Critical" },
];
const PRIO_OPTS = [
  { v: "low", label: "Low" },
  { v: "normal", label: "Normal" },
  { v: "high", label: "High" },
  { v: "urgent", label: "Urgent" },
];

const REF_TYPES = [
  { v: "project", label: "Project" },
  { v: "asset", label: "Asset" },
  { v: "wo", label: "Working Order" },
  { v: "invoice", label: "Invoice" },
  { v: "tender", label: "Tender" },
  { v: "reminder", label: "Reminder/Event" },
  { v: "task", label: "Task" },
  { v: "integrity_finding", label: "Data Integrity Finding" },
];

const DIVISIONS = ["BOD", "FAT", "HRGA", "Procurement Asset Logistic", "Project"];

export default function IssueDrawer({ open, onOpenChange, existing = null, categories = [], onSaved }) {
  const isEdit = !!existing?.id;
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(() => buildInitial(existing));
  useEffect(() => { if (open) setForm(buildInitial(existing)); }, [open, existing]);

  useEffect(() => {
    if (!open) return;
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setImpact = (k, v) => setForm((f) => ({ ...f, impacts: { ...(f.impacts || {}), [k]: v } }));

  const submit = async () => {
    if (!form.title?.trim()) { toast.error("Judul wajib diisi"); return; }
    setBusy(true);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category || null,
        project_id: form.project_id || null,
        division: form.division || null,
        description: form.description || null,
        impacts: form.impacts || {},
        severity: form.severity || "medium",
        priority: form.priority || "normal",
        pic_id: form.pic_id || null,
        pic_name: form.pic_name || null,
        target_resolve_date: form.target_resolve_date || null,
        references: form.references || [],
      };
      let iid = existing?.id;
      if (isEdit) {
        await api.patch(`/critical-issues/${existing.id}`, payload);
        toast.success("Issue diperbarui");
      } else {
        const { data } = await api.post("/critical-issues", { ...payload, source: "manual" });
        iid = data.id;
        toast.success(`Issue ${data.code} dibuat`);
      }
      // Upload pending files if any
      if (iid && form._pendingFiles?.length) {
        for (const f of form._pendingFiles) {
          const fd = new FormData();
          fd.append("file", f);
          try { await api.post(`/critical-issues/${iid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
          catch (e) { toast.error(`Upload ${f.name} gagal: ${formatDetail(e.response?.data?.detail)}`); }
        }
      }
      onOpenChange(false);
      onSaved?.(iid);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal simpan");
    } finally { setBusy(false); }
  };

  const removeAttachment = async (att) => {
    if (!isEdit) {
      // pending file — remove from local
      setForm((f) => ({ ...f, _pendingFiles: (f._pendingFiles || []).filter((x) => x !== att) }));
      return;
    }
    if (!window.confirm(`Hapus lampiran ${att.name}?`)) return;
    try {
      await api.post(`/critical-issues/${existing.id}/attachments/remove`, { storage_path: att.storage_path });
      toast.success("Lampiran dihapus");
      onSaved?.(existing.id);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const addRef = (type, id, label) => {
    const refs = form.references || [];
    if (refs.find((r) => r.type === type && r.id === id)) { toast.info("Referensi sudah ada"); return; }
    set("references", [...refs, { type, id, label }]);
  };
  const removeRef = (r) => set("references", (form.references || []).filter((x) => !(x.type === r.type && x.id === r.id)));

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="issue-drawer">
      <div className="flex-1 bg-black/40" onClick={() => onOpenChange(false)} />
      <aside className="w-full max-w-2xl bg-white h-full shadow-2xl overflow-y-auto flex flex-col">
        <header className="sticky top-0 bg-[#0B1D3A] text-white px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/60">Critical Issue</div>
            <h2 className="font-display text-lg">{isEdit ? `Edit ${existing.code}` : "Issue Baru"}</h2>
          </div>
          <button type="button" onClick={() => onOpenChange(false)} data-testid="issue-drawer-close" className="text-white/70 hover:text-white"><X size={20} /></button>
        </header>

        <div className="flex-1 px-5 py-4 space-y-6">
          <Section icon={Info} title="Informasi Utama" testid="section-info">
            {isEdit && (
              <div className="text-[11px] text-slate-500">
                Issue ID: <span className="font-mono font-semibold text-[#0B1D3A]">{existing.code}</span>{" "}
                <span className="text-slate-400">(otomatis)</span>
              </div>
            )}
            <FieldRow label="Judul *">
              <Input data-testid="issue-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-none" />
            </FieldRow>
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Kategori">
                <select data-testid="issue-category" value={form.category || ""} onChange={(e) => set("category", e.target.value)} className="w-full h-9 border border-slate-300 px-2 text-xs bg-white">
                  <option value="">— Pilih —</option>
                  {categories.filter((c) => c.is_active !== false).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Divisi Terkait">
                <select data-testid="issue-division" value={form.division || ""} onChange={(e) => set("division", e.target.value)} className="w-full h-9 border border-slate-300 px-2 text-xs bg-white">
                  <option value="">— Pilih —</option>
                  {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </FieldRow>
            </div>
            <FieldRow label="Project Terkait">
              <ProjectPicker value={form.project_id} onChange={(id) => set("project_id", id)} />
            </FieldRow>
            <FieldRow label="Deskripsi">
              <textarea data-testid="issue-description" value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={3} className="w-full border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:border-[#FF5A1F]" />
            </FieldRow>
          </Section>

          <Section icon={AlertTriangle} title="Dampak & Prioritas" testid="section-impact">
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Severity">
                <select data-testid="issue-severity" value={form.severity} onChange={(e) => set("severity", e.target.value)} className="w-full h-9 border border-slate-300 px-2 text-xs bg-white">
                  {SEV_OPTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Prioritas">
                <select data-testid="issue-priority" value={form.priority} onChange={(e) => set("priority", e.target.value)} className="w-full h-9 border border-slate-300 px-2 text-xs bg-white">
                  {PRIO_OPTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </FieldRow>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Dampak Biaya"><textarea data-testid="impact-cost" value={form.impacts?.cost || ""} onChange={(e) => setImpact("cost", e.target.value)} rows={2} className="w-full border border-slate-300 px-2 py-1.5 text-xs" /></FieldRow>
              <FieldRow label="Dampak Jadwal"><textarea data-testid="impact-schedule" value={form.impacts?.schedule || ""} onChange={(e) => setImpact("schedule", e.target.value)} rows={2} className="w-full border border-slate-300 px-2 py-1.5 text-xs" /></FieldRow>
              <FieldRow label="Dampak Client"><textarea data-testid="impact-client" value={form.impacts?.client || ""} onChange={(e) => setImpact("client", e.target.value)} rows={2} className="w-full border border-slate-300 px-2 py-1.5 text-xs" /></FieldRow>
              <FieldRow label="Dampak Operasional"><textarea data-testid="impact-ops" value={form.impacts?.ops || ""} onChange={(e) => setImpact("ops", e.target.value)} rows={2} className="w-full border border-slate-300 px-2 py-1.5 text-xs" /></FieldRow>
            </div>
          </Section>

          <Section icon={UserCog} title="Kepemilikan & Target" testid="section-ownership">
            <FieldRow label="PIC">
              <UserPicker users={users} value={form.pic_id} onChange={(uid, uname) => setForm((f) => ({ ...f, pic_id: uid, pic_name: uname }))} />
            </FieldRow>
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Target Selesai"><Input data-testid="issue-target" type="date" value={form.target_resolve_date || ""} onChange={(e) => set("target_resolve_date", e.target.value)} className="rounded-none" /></FieldRow>
              {isEdit && (
                <FieldRow label="Status Saat Ini">
                  <div className="h-9 px-2 border border-slate-200 bg-slate-50 text-xs flex items-center capitalize">{existing?.status?.replace("_", " ")}</div>
                </FieldRow>
              )}
            </div>
          </Section>

          <Section icon={Paperclip} title="Referensi & Lampiran" testid="section-references">
            <RefPicker onAdd={addRef} />
            {(form.references || []).length > 0 && (
              <ul className="border border-slate-200 divide-y divide-slate-100 text-xs" data-testid="issue-refs-list">
                {(form.references || []).map((r) => (
                  <li key={`${r.type}-${r.id}`} className="flex items-center gap-2 px-2 py-1.5">
                    <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200">{REF_TYPES.find((t) => t.v === r.type)?.label || r.type}</span>
                    <span className="flex-1 truncate text-slate-700" title={r.label}>{r.label || r.id.slice(0, 12) + "…"}</span>
                    <button type="button" onClick={() => removeRef(r)} className="text-rose-600 hover:text-rose-800 px-1" title="Hapus"><Trash2 size={12} /></button>
                  </li>
                ))}
              </ul>
            )}

            <div>
              <Label className="overline text-slate-600">Lampiran</Label>
              <FileUploader onFiles={(files) => setForm((f) => ({ ...f, _pendingFiles: [...(f._pendingFiles || []), ...files] }))} />
              {((existing?.attachments || []).length > 0 || (form._pendingFiles || []).length > 0) && (
                <ul className="mt-2 border border-slate-200 divide-y divide-slate-100 text-xs" data-testid="issue-atts-list">
                  {(existing?.attachments || []).map((a) => (
                    <li key={a.storage_path} className="flex items-center gap-2 px-2 py-1.5">
                      <Paperclip size={12} className="text-slate-500" />
                      <a href={`${(api.defaults?.baseURL || "").replace(/\/api$/, "")}${a.url}`} target="_blank" rel="noreferrer" className="flex-1 truncate text-sky-700 hover:underline">{a.name}</a>
                      <span className="text-[10px] text-slate-500">{Math.round((a.size || 0) / 1024)} KB</span>
                      <button type="button" onClick={() => removeAttachment(a)} className="text-rose-600 hover:text-rose-800 px-1"><Trash2 size={12} /></button>
                    </li>
                  ))}
                  {(form._pendingFiles || []).map((f) => (
                    <li key={f.name + f.size} className="flex items-center gap-2 px-2 py-1.5 bg-amber-50">
                      <Upload size={12} className="text-amber-600" />
                      <span className="flex-1 truncate text-slate-700">{f.name}</span>
                      <span className="text-[10px] text-amber-700 uppercase font-semibold">pending upload</span>
                      <button type="button" onClick={() => removeAttachment(f)} className="text-rose-600 hover:text-rose-800 px-1"><Trash2 size={12} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Section>

          <Section icon={ListChecks} title="Tindakan Lanjutan" testid="section-followup">
            <p className="text-[10px] text-slate-500 italic">
              Follow-up formal (dengan owner & due date, immutable per versi) dicatat via <b>Record Decision</b> setelah issue dibuat.
              Bagian ini untuk catatan tindakan awal / preliminary — akan disimpan sebagai bagian deskripsi.
            </p>
            <textarea
              data-testid="issue-followup-notes"
              value={form.followup_notes || ""}
              onChange={(e) => {
                set("followup_notes", e.target.value);
                // Append/replace to description tail
                const base = (form.description || "").replace(/\n\n\[TINDAKAN AWAL\][\s\S]*/, "");
                set("description", e.target.value.trim() ? `${base}\n\n[TINDAKAN AWAL]\n${e.target.value}` : base);
              }}
              rows={3}
              placeholder="Contoh: koordinasi dgn Legal minggu ini · siapkan draft surat somasi · dst"
              className="w-full border border-slate-300 px-2 py-1.5 text-xs" />
          </Section>
        </div>

        <footer className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button data-testid="issue-drawer-submit" onClick={submit} disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold">
            {busy ? <><Loader2 size={13} className="mr-1 animate-spin" />Menyimpan…</> : (isEdit ? "Simpan Perubahan" : "Simpan Issue")}
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function buildInitial(existing) {
  if (!existing) {
    return {
      title: "", category: "", project_id: "", division: "",
      description: "", impacts: {},
      severity: "medium", priority: "normal",
      pic_id: "", pic_name: "", target_resolve_date: "",
      references: [], _pendingFiles: [], followup_notes: "",
    };
  }
  return {
    title: existing.title || "",
    category: existing.category || "",
    project_id: existing.project_id || "",
    division: existing.division || "",
    description: existing.description || "",
    impacts: existing.impacts || {},
    severity: existing.severity || "medium",
    priority: existing.priority || "normal",
    pic_id: existing.pic_id || "",
    pic_name: existing.pic_name || "",
    target_resolve_date: existing.target_resolve_date || "",
    references: existing.references || [],
    _pendingFiles: [], followup_notes: "",
  };
}

function Section({ icon: Icon, title, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-200">
        <Icon size={14} className="text-[#FF5A1F]" />
        <h3 className="text-[11px] uppercase tracking-wider font-semibold text-[#0B1D3A]">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="overline text-slate-600 text-[10px]">{label}</Label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function ProjectPicker({ value, onChange }) {
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/active-projects").then(({ data }) => setProjects(data)).catch(() => {}); }, []);
  return (
    <select value={value || ""} data-testid="issue-project" onChange={(e) => onChange(e.target.value)} className="w-full h-9 border border-slate-300 px-2 text-xs bg-white">
      <option value="">— Tidak terikat project —</option>
      {projects.map((p) => (
        <option key={p.id} value={p.id}>{p.project_name || p.wo_number || p.variation_order_name || p.id.slice(0, 8)}</option>
      ))}
    </select>
  );
}

function UserPicker({ users, value, onChange }) {
  return (
    <select value={value || ""} data-testid="issue-pic" onChange={(e) => {
      const uid = e.target.value; const u = users.find((x) => x.id === uid);
      onChange(uid, u?.name || null);
    }} className="w-full h-9 border border-slate-300 px-2 text-xs bg-white">
      <option value="">— Belum di-assign —</option>
      {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.division} Lv{u.level}</option>)}
    </select>
  );
}

function RefPicker({ onAdd }) {
  const [type, setType] = useState("project");
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const debTimer = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setOpts([]); return; }
    clearTimeout(debTimer.current);
    debTimer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = ({
          project: "/active-projects",
          asset: "/assets",
          wo: "/active-projects?project_type=working_order",
          tender: "/tenders",
          reminder: "/reminders/events",
          task: "/tasks",
          integrity_finding: null,
          invoice: "/collection/invoices",
        })[type];
        if (!url) { setOpts([]); setLoading(false); return; }
        const { data } = await api.get(url);
        const items = Array.isArray(data) ? data : (data.items || data.events || []);
        const qq = q.toLowerCase();
        const filtered = items.filter((it) => JSON.stringify(it).toLowerCase().includes(qq)).slice(0, 15);
        setOpts(filtered.map((it) => normalize(type, it)));
      } catch { setOpts([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(debTimer.current);
  }, [q, type]);

  return (
    <div className="border border-dashed border-slate-300 p-2 space-y-1.5" data-testid="issue-ref-picker">
      <div className="flex gap-1.5">
        <select value={type} onChange={(e) => { setType(e.target.value); setQ(""); setOpts([]); }} className="h-8 border border-slate-300 px-2 text-xs bg-white">
          {REF_TYPES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
        </select>
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Cari ${REF_TYPES.find((r) => r.v === type)?.label || ""}…`}
          className="flex-1 h-8 px-2 border border-slate-300 text-xs" />
      </div>
      {loading && <div className="text-[10px] text-slate-500 italic">Mencari…</div>}
      {!loading && opts.length > 0 && (
        <ul className="max-h-40 overflow-y-auto border border-slate-200 bg-white">
          {opts.map((o) => (
            <li key={o.id} className="px-2 py-1 hover:bg-slate-50 flex items-center justify-between">
              <span className="text-xs text-slate-700 truncate flex-1" title={o.label}>{o.label}</span>
              <button type="button" onClick={() => { onAdd(type, o.id, o.label); setQ(""); setOpts([]); }} className="text-[10px] uppercase tracking-wider font-semibold text-sky-700 hover:text-sky-900 px-1">+ Add</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function normalize(type, it) {
  const id = it.id || it.entry_id || it.entry_key;
  let label = "";
  switch (type) {
    case "project":
    case "wo":
      label = it.project_name || it.wo_number || it.variation_order_name || id;
      break;
    case "asset":
      label = it.name || it.asset_tag || id;
      break;
    case "tender":
      label = it.project_name || it.title || id;
      break;
    case "reminder":
      label = `${it.title} (${it.event_date || ""})`;
      break;
    case "task":
      label = it.title || id;
      break;
    case "invoice":
      label = `${it.invoice_number || "—"} · ${it.client || ""} · ${it.project_name || ""}`;
      break;
    default:
      label = it.title || id;
  }
  return { id, label };
}

function FileUploader({ onFiles }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); onFiles(Array.from(e.dataTransfer.files || [])); }}
      className={`border-2 border-dashed p-3 text-center text-xs cursor-pointer ${drag ? "border-[#FF5A1F] bg-orange-50" : "border-slate-300 hover:border-[#FF5A1F]"}`}
      onClick={() => inputRef.current?.click()}
      data-testid="issue-upload-zone"
    >
      <Upload size={16} className="mx-auto text-slate-400 mb-1" />
      <div className="text-slate-600">Drag file ke sini atau klik untuk pilih</div>
      <div className="text-[10px] text-slate-400 mt-1">Maks 10 MB · gambar, PDF, dokumen Office, csv</div>
      <input ref={inputRef} type="file" multiple hidden onChange={(e) => onFiles(Array.from(e.target.files || []))} />
    </div>
  );
}
